<template>
  <div
    class="date-picker"
    @mouseleave="onMouseLeave"
  >
    <div>
      <ul class="date-picker__list">
        <li
          v-for="(period, index) in relativePeriodsList()"
          :key="index"
          :class="{'date-picker__list-item--active': activePeriodIndex === index}"
          class="date-picker__list-item"
          @click="moveCalendar(period, index)"
        >
          {{ period.label }}
        </li>
      </ul>
    </div>
    <div>
      <div>
        <vc-date-picker
          v-model="range"
          :columns="2"
          :update-on-input="true"
          :min-date="minDate"
          :max-date="maxDate"
          locale="pt-BR"
          color="purple"
          mode="date"
          is24hr
          is-range
          @dayclick="onDayClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  format,
  formatISO,
  parseISO,
  sub,
  startOfDay,
  endOfDay,
} from 'date-fns';

const today = new Date();
const sixDaysAgo = sub(today, { days: 6 });
const twelveMonthsAgo = sub(new Date(), { months: 12 });

export default {
  name: 'DatePickerWithDateList',
  components: {
  },
  props: {
    initialDate: {
      type: Object,
      default() {
        return {
          start: formatISO(startOfDay(sixDaysAgo)),
          end: formatISO(endOfDay(today)),
        };
      },
    },
    periodList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activePeriodIndex: -1,
      dateRange: {
        start: formatISO(startOfDay(sixDaysAgo)),
        end: formatISO(endOfDay(today)),
      },
      temporaryRange: {
        start: null,
        end: null,
      },
      previousDate: {},
      minDate: formatISO(startOfDay(twelveMonthsAgo)),
      maxDate: formatISO(endOfDay(today)),
      days: [],
    };
  },
  computed: {
    range: {
      get() {
        return {
          start: this.dateRange.start,
          end: this.dateRange.end,
        };
      },
      set(newDate) {
        this.temporaryRange.start = formatISO(newDate.start);
        this.temporaryRange.end = formatISO(newDate.end);
      },
    },
    startRange() {
      return this.temporaryRange.start;
    },
    endRange() {
      return this.temporaryRange.end;
    },
  },
  watch: {
    startRange() {
      this.changeActivePeriodIndex();
    },
    endRange() {
      this.changeActivePeriodIndex();
    },
  },
  mounted() {
    this.dateRange = this.initialDate;
    this.temporaryRange = { ...this.initialDate };
    this.previousDate = { ...this.initialDate };
  },
  methods: {
    onDayClick(day) {
      this.days.push(day.id);

      if (this.days.length === 2) {
        this.setDateRange();
      }
    },
    onMouseLeave(e) {
      if (this.days.length === 1) return;

      if (this.days.length > 1) return this.setDateRange();

      this.$emit('mouseLeaveFromDatePickerExpanded', e);
    },
    moveCalendar(period) {
      period.moveToSpecificPeriod.bind(this)();
    },
    relativePeriodsList() {
      return this.periodList.map((period) => ({
        label: period.label,
        moveToSpecificPeriod() {
          this.dateRange.start = period.start;
          this.dateRange.end = period.end;

          this.$emit('period-selected', {
            date: this.dateRange,
            activePeriodIndex: this.activePeriodIndex,
            label: period.label,
          });
        },
      }));
    },
    dateRangeFormatter(date) {
      const start = format(parseISO(date.start), 'dd/LL/yyyy');
      const end = format(parseISO(date.end), 'dd/LL/yyyy');

      return `${start}:${end}`;
    },
    changeActivePeriodIndex() {
      const currentDateRange = this.dateRangeFormatter(this.temporaryRange);

      this.activePeriodIndex = this.periodList.findIndex((period) => {
        const periodDateRange = this.dateRangeFormatter(period);
        return periodDateRange === currentDateRange;
      });
    },
    setDateRange() {
      this.dateRange.start = this.temporaryRange.start;
      this.dateRange.end = this.temporaryRange.end;

      this.$emit('date-selected', {
        date: this.dateRange,
        activePeriodIndex: this.activePeriodIndex,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.date-picker {
  width: 697px;
  display: flex;
  box-sizing: border-box;
  z-index: var(--zindex-1000);
  justify-content: space-between;
  padding: var(--ds-spacing-stack-sm);
  box-shadow: var(--ds-light-dropshadow-1);
  border-radius: var(--ds-border-radius-sm);
  background-color: var(--ds-color-neutral);

  ::v-deep {
    .vc-arrows-container + div {
      display: flex;
      height: 80px;
      justify-content: space-between;
    }

    .vc-header,
    .vc-arrows-container {
      padding-top: 0;
    }

    .vc-title {
      font-size: var(--ds-font-size-baseline);
    }

    .vc-weekday {
      font-size: var(--ds-font-size-xs);
    }

    .vc-pane-container div:nth-child(3) {
      display: flex;
      justify-content: space-around;
    }

    .vc-pane-container {
      border: none;
    }

    .vc-time-picker.vc-bordered {
      width: 100%;
      justify-content: center;
      border-color: transparent;
    }

    .vc-pane-layout {
      max-height: 265px;
    }

    .vc-day-content {
      font-size: var(--ds-font-size-xs);
      font-family: var(--ds-font-family);
      line-height: var(--ds-font-size-baseline);
      font-weight: var(--ds-font-weight-regular);
    }

    .vc-container {
      border: none;
    }
  }

  &__list {
    margin: 0;
    padding: 0;
    height: 100%;

    &-item {
      width: 128px;
      height: 40px;
      cursor: pointer;
      display: flex;
      align-items: center;
      color: var(--ds-color-text-1);
      font-size: var(--ds-font-size-sm);
      padding: 10px var(--ds-spacing-inset-nano);
      border-radius: var(--ds-border-radius-sm);
      font-weight: var(--ds-font-weight-regular);

      &:not(&--active):hover {
        background: rgba(18, 18, 18, 0.16);
      }

      &--active {
        color: var(--ds-color-interactive);
        background: rgb(229, 229, 251);
      }
    }
  }
}
</style>
